import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import type { AnnouncementFormData } from '@/types/announcement';
import { CustomLayoutRenderer } from './custom-layouts/CustomLayoutRenderer';

interface LiveTemplatePreviewProps {
  formData: AnnouncementFormData;
  className?: string;
  chibiUrl?: string | null;
}

export function LiveTemplatePreview({ formData, className, chibiUrl }: LiveTemplatePreviewProps) {
  const isCustom = formData.template === 'custom';

  if (isCustom) {
    return (
      <div className={cn('rounded-xl overflow-hidden shadow-lg border border-border', className)}>
        <CustomLayoutRenderer formData={formData} />
      </div>
    );
  }

  const isEncouragement = formData.template === 'encouragement';

  // Non-custom templates: simple preview (unchanged behavior)
  const previewText = formData.message?.trim() || (isEncouragement
    ? "I see how hard you've been working, and I want you to know — you're doing amazing. Keep going. 💪"
    : "There are things I should have said sooner. Thank you for carrying me through moments\nI didn't even know were heavy.");
  const recipientName = formData.recipientName?.trim() || 'Someone Special';

  const templateBgClass = (() => {
    switch (formData.template) {
      case 'apology': return 'bg-gradient-to-b from-slate-50 via-stone-50 to-zinc-100';
      case 'gratitude': return 'bg-gradient-to-br from-amber-50 via-orange-50/80 to-yellow-50';
      case 'hard_conversation': return 'bg-gradient-to-b from-sky-100 via-blue-50/60 to-rose-50/40';
      case 'love': return 'bg-gradient-to-br from-rose-100/90 via-purple-50/70 to-amber-50/80';
      case 'closure': return 'bg-gradient-to-br from-amber-100 via-yellow-50/90 to-orange-100/80';
      case 'encouragement': return 'bg-gradient-to-br from-emerald-100 via-cyan-50 to-sky-100';
      default: return 'bg-gradient-to-b from-slate-50 to-slate-100';
    }
  })();

  const templateTextClass = (() => {
    switch (formData.template) {
      case 'apology': return 'text-slate-800';
      case 'gratitude': return 'text-amber-900';
      case 'hard_conversation': return 'text-slate-800';
      case 'love': return 'text-rose-900';
      case 'closure': return 'text-amber-900';
      case 'encouragement': return 'text-emerald-900';
      default: return 'text-slate-800';
    }
  })();

  if (isEncouragement) {
    return (
      <div className={cn('relative rounded-xl overflow-hidden shadow-lg border border-border', templateBgClass, className)}>
        {/* Animated floating elements for "you got this" energy */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {['✨', '⭐', '💪', '🌟', '🔥'].map((emoji, i) => (
            <motion.span
              key={i}
              className="absolute text-lg opacity-40"
              style={{
                left: `${15 + i * 18}%`,
                top: `${10 + (i % 3) * 25}%`,
              }}
              animate={{
                y: [0, -8, 0],
                rotate: [0, 10, -10, 0],
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 2.5 + i * 0.3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.4,
              }}
            >
              {emoji}
            </motion.span>
          ))}
        </div>

        <div className="relative z-20 p-6 md:p-8">
          {/* Bold encouragement header */}
          <motion.div
            className="text-center mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-emerald-600 font-semibold mb-2">
              You Got This 💪
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-emerald-900 tracking-tight">
              For {recipientName}
            </h2>
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="w-12 h-0.5 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400" />
              <span className="text-emerald-500">✦</span>
              <div className="w-12 h-0.5 rounded-full bg-gradient-to-l from-emerald-400 to-cyan-400" />
            </div>
          </motion.div>

          {/* Chibi display area */}
          {chibiUrl && (
            <motion.div
              className="mx-auto mb-4 max-w-[200px]"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', damping: 15 }}
            >
              <img
                src={chibiUrl}
                alt="Mini chibi encouragement characters"
                className="w-full h-auto rounded-lg shadow-md"
              />
            </motion.div>
          )}

          {/* Message with uplifting styling */}
          <div className="max-w-md mx-auto text-center">
            <motion.p
              className="text-sm md:text-base leading-relaxed whitespace-pre-wrap text-emerald-800 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {previewText}
            </motion.p>
          </div>

          {/* Bright sign-off */}
          <div className="mt-5 text-center">
            <p className="text-xs text-emerald-500 font-semibold tracking-wide">
              Keep Going • You're Amazing ✨
            </p>
            <p className="text-[10px] opacity-30 tracking-wider mt-1">Say It NOW • While They're Still Here</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative rounded-xl overflow-hidden shadow-lg border border-border', templateBgClass, className)}>
      <div className="relative z-20 p-6 md:p-8">
        <div className="text-center mb-6 font-serif">
          <p className="text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">A message for you</p>
          <h2 className={cn('text-xl md:text-2xl tracking-wide', templateTextClass, 'font-bold')}>
            For {recipientName}
          </h2>
          <div className="flex items-center justify-center gap-3 mt-3">
            <div className="w-10 h-px bg-current opacity-20" />
            <div className="w-1.5 h-1.5 rounded-full bg-current opacity-30" />
            <div className="w-10 h-px bg-current opacity-20" />
          </div>
        </div>
        <div className="max-w-md mx-auto text-center font-serif">
          <p className={cn('text-sm md:text-base leading-relaxed whitespace-pre-wrap', templateTextClass)} style={{ opacity: 0.85 }}>
            {previewText}
          </p>
        </div>
        <div className="mt-6 text-center">
          <p className="text-[10px] opacity-30 tracking-wider">Say It NOW • While They're Still Here</p>
        </div>
      </div>
    </div>
  );
}
